import {ModuleComponent} from "../../js/module-component";

/**
 * "Antes y después" (SEION) — comparador de dos estados.
 *  - Alterna solo entre el estado 0 ("antes") y el 1 ("después") con un único
 *    setTimeout que se reprograma.
 *  - Clic en una pestaña fija el estado y reinicia el temporizador.
 *  - El ratón sobre el módulo pausa el temporizador y las barras.
 *
 * Del handoff: la entrada es animación CSS directa (no una clase mutada por
 * IntersectionObserver) y la barra de progreso se reinicia relanzando la
 * animación, nunca desde un contador en el estado. Las imágenes solo cambian
 * de clase para el crossfade; no se remontan.
 */
export class ModAgentsEight extends ModuleComponent{

    ready(){
        super.ready();
        this.el = this.$root.get(0);
        if(!this.el) return;

        this.scope = "web-mod-agents-eight";
        this.reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

        const dwell = parseInt(this.el.dataset.dwell || "", 10);
        this.dwell = Math.min(12000, Math.max(2500, isNaN(dwell) ? 6000 : dwell));

        this.root = this.el.querySelector(".js-root");
        this.stage = this.el.querySelector(".js-stage");
        this.tabs = Array.from(this.el.querySelectorAll(".js-tab"));
        this.layers = Array.from(this.el.querySelectorAll(".js-layer"));
        this.chips = Array.from(this.el.querySelectorAll(".js-chip"));
        this.state = this.el.querySelector(".js-state");
        this.stateLabel = this.el.querySelector(".js-state-label");
        this.counter = this.el.querySelector(".js-counter");

        if(this.tabs.length < 2) return;

        this.labels = this.tabs.map((t) => {
            const l = t.querySelector("." + this.scope + "__tab-label");
            return l ? l.textContent.trim() : "";
        });

        this.active = 0;
        this.paused = false;

        this.tabs.forEach((tab) => {
            tab.addEventListener("click", () => this.pick(Number(tab.dataset.index || 0)));
        });

        if(this.root){
            this.onEnter = () => { clearTimeout(this.timer); this.paused = true; this.setBarState("paused"); };
            this.onLeave = () => { this.paused = false; this.setBarState("running"); this.schedule(); };
            this.root.addEventListener("pointerenter", this.onEnter);
            this.root.addEventListener("pointerleave", this.onLeave);
        }

        this.updateBars();
        this.schedule();
    }

    schedule(){
        if(this.reduceMotion) return;
        clearTimeout(this.timer);
        this.timer = setTimeout(() => this.pick(this.active === 0 ? 1 : 0), this.dwell);
    }

    pick(i){
        if(i !== 0 && i !== 1) return;
        this.active = i;
        this.updateTabs();
        this.updateStage();
        this.updateBars();
        if(!this.paused) this.schedule();
    }

    updateTabs(){
        this.tabs.forEach((tab, i) => {
            const on = i === this.active;
            tab.classList.toggle("is-active", on);
            tab.setAttribute("aria-pressed", on ? "true" : "false");
        });
    }

    updateStage(){
        if(this.stage) this.stage.classList.toggle("is-after", this.active === 1);

        this.layers.forEach((layer, i) => {
            const on = i === this.active;
            layer.classList.toggle("is-active", on);
            if(on){ layer.removeAttribute("aria-hidden"); }
            else { layer.setAttribute("aria-hidden", "true"); }
        });

        this.chips.forEach((chip) => {
            chip.classList.toggle("is-hidden", Number(chip.dataset.index || 0) !== this.active);
        });

        if(this.stateLabel) this.stateLabel.textContent = this.labels[this.active] || "";
        if(this.counter) this.counter.textContent = "0" + (this.active + 1) + " / 02";

        // Remonta la animación de entrada de la cabecera y de los chips visibles.
        this.restart(this.state);
        this.chips.forEach((chip) => { if(!chip.classList.contains("is-hidden")) this.restart(chip); });
    }

    // Barra de progreso: se reinicia relanzando la animación (no vive en state).
    updateBars(){
        this.tabs.forEach((tab, i) => {
            const fill = tab.querySelector(".js-bar-fill");
            if(!fill) return;
            if(i === this.active && !this.reduceMotion){
                fill.style.animation = "none";
                void fill.offsetWidth;                 // fuerza reflow para reiniciar
                fill.style.animation = "a08Bar " + this.dwell + "ms linear forwards";
                fill.style.animationPlayState = this.paused ? "paused" : "running";
            }else{
                fill.style.animation = "none";
                fill.style.transform = "scaleX(0)";
            }
        });
    }

    setBarState(state){
        const tab = this.tabs[this.active];
        const fill = tab && tab.querySelector(".js-bar-fill");
        if(fill) fill.style.animationPlayState = state;
    }

    // Reinicia una animación CSS ya definida por clase.
    restart(el){
        if(!el || this.reduceMotion) return;
        el.style.animation = "none";
        void el.offsetWidth;
        el.style.animation = "";
    }

    destroy(){
        clearTimeout(this.timer);
        if(this.root && this.onEnter){
            this.root.removeEventListener("pointerenter", this.onEnter);
            this.root.removeEventListener("pointerleave", this.onLeave);
        }
        if(super.destroy) super.destroy();
    }
}
